/**
 * VideoPlayer (Sprint 3 B5) — Vidstack wrapper cho HLS streaming.
 *
 * Sếp đã chọn Vidstack (vs Video.js) — React-first, TypeScript types built-in,
 * slot-based overlay system, mặc định kéo theo hls.js cho HLS streaming.
 *
 * Default layout (`DefaultVideoLayout`) cho ra UI tương đương YouTube/Vimeo:
 * play/pause, scrub bar, volume, fullscreen, settings (speed + quality).
 * Sprint 4 sẽ thêm: chapter markers, transcript, watch progress overlay.
 *
 * Note: import 2 CSS file — theme + video layout. Vite handle bundle.
 */

import { useRef } from "react";
import { MediaPlayer, MediaProvider, Poster } from "@vidstack/react";
import {
  DefaultVideoLayout,
  defaultLayoutIcons,
} from "@vidstack/react/player/layouts/default";

import "@vidstack/react/player/styles/default/theme.css";
import "@vidstack/react/player/styles/default/layouts/video.css";

interface Props {
  /** HLS .m3u8 URL (đã signed). */
  src: string;
  /** Thumbnail URL (optional). */
  poster?: string;
  /** Tiêu đề hiện trên header player. */
  title?: string;
  /** Resume từ thời điểm này khi player ready (giây). null = play từ đầu. */
  startTime?: number | null;
  /** Callback khi user xem hết video — Sprint 6 dùng để mark complete. */
  onEnded?: () => void;
  /**
   * Callback định kỳ — Sprint 6 dùng để track progress.
   *
   * `duration` lấy từ event `onDurationChange` riêng (Vidstack split: timeupdate
   * chỉ có currentTime, duration là event độc lập emit khi metadata loaded).
   * Em cache duration vào ref → mỗi tick timeupdate đều có duration mới nhất.
   */
  onTimeUpdate?: (currentTime: number, duration: number) => void;
}

export function VideoPlayer({
  src,
  poster,
  title,
  startTime,
  onEnded,
  onTimeUpdate,
}: Props) {
  const durationRef = useRef<number>(0);

  return (
    <MediaPlayer
      src={src}
      title={title}
      poster={poster}
      crossOrigin
      playsInline
      currentTime={startTime ?? 0}
      onEnded={onEnded}
      onDurationChange={(duration) => {
        durationRef.current = duration;
      }}
      onTimeUpdate={(detail) => {
        if (onTimeUpdate) onTimeUpdate(detail.currentTime, durationRef.current);
      }}
      style={{
        width: "100%",
        aspectRatio: "16 / 9",
        backgroundColor: "#000",
        borderRadius: 8,
        overflow: "hidden",
      }}
      data-testid="video-player"
    >
      <MediaProvider>
        {poster ? <Poster className="vds-poster" src={poster} alt={title ?? "video"} /> : null}
      </MediaProvider>
      <DefaultVideoLayout icons={defaultLayoutIcons} />
    </MediaPlayer>
  );
}
